import { useState } from 'react';
import type { PickerProps } from './types';

interface CookieAttrs {
  nameValue: string;
  path: string;
  domain: string;
  secure: boolean;
  httpOnly: boolean;
  sameSite: 'Lax' | 'Strict' | 'None' | '';
  maxAge: string;
}

function parse(value: string): CookieAttrs {
  const parts = value.split(';').map((p) => p.trim());
  const nameValue = parts[0] ?? '';
  const find = (key: string): string | undefined => {
    const hit = parts
      .slice(1)
      .find(
        (p) =>
          p.toLowerCase().startsWith(key.toLowerCase() + '=') ||
          p.toLowerCase() === key.toLowerCase(),
      );
    if (!hit) return undefined;
    const eq = hit.indexOf('=');
    return eq === -1 ? '' : hit.slice(eq + 1);
  };
  const has = (key: string): boolean =>
    parts.slice(1).some((p) => p.toLowerCase() === key.toLowerCase());

  const ss = find('SameSite');
  return {
    nameValue,
    path: find('Path') ?? '/',
    domain: find('Domain') ?? '',
    secure: has('Secure'),
    httpOnly: has('HttpOnly'),
    sameSite: (ss === 'Lax' || ss === 'Strict' || ss === 'None'
      ? ss
      : '') as CookieAttrs['sameSite'],
    maxAge: find('Max-Age') ?? '',
  };
}

function build(a: CookieAttrs): string {
  const parts: string[] = [a.nameValue || 'session=abc'];
  if (a.path) parts.push(`Path=${a.path}`);
  if (a.domain) parts.push(`Domain=${a.domain}`);
  if (a.maxAge) parts.push(`Max-Age=${a.maxAge}`);
  if (a.secure) parts.push('Secure');
  if (a.httpOnly) parts.push('HttpOnly');
  if (a.sameSite) parts.push(`SameSite=${a.sameSite}`);
  return parts.join('; ');
}

export function SetCookiePicker({ value, onChange }: PickerProps) {
  const [attrs, setAttrs] = useState<CookieAttrs>(() => parse(value));

  const update = <K extends keyof CookieAttrs>(key: K, val: CookieAttrs[K]) => {
    const next = { ...attrs, [key]: val };
    setAttrs(next);
    onChange(build(next));
  };

  return (
    <div className="picker-grid">
      <input
        className="picker-input"
        placeholder="name=value"
        value={attrs.nameValue}
        onChange={(e) => update('nameValue', e.target.value)}
      />
      <div className="picker-row">
        <input
          className="picker-input"
          placeholder="Path"
          value={attrs.path}
          onChange={(e) => update('path', e.target.value)}
        />
        <input
          className="picker-input"
          placeholder="Domain"
          value={attrs.domain}
          onChange={(e) => update('domain', e.target.value)}
        />
        <input
          className="picker-input"
          placeholder="Max-Age"
          value={attrs.maxAge}
          onChange={(e) => update('maxAge', e.target.value)}
        />
      </div>
      <div className="picker-row">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={attrs.secure}
            onChange={(e) => update('secure', e.target.checked)}
          />
          Secure
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={attrs.httpOnly}
            onChange={(e) => update('httpOnly', e.target.checked)}
          />
          HttpOnly
        </label>
        <select
          value={attrs.sameSite}
          onChange={(e) => update('sameSite', e.target.value as CookieAttrs['sameSite'])}
        >
          <option value="">SameSite: (none set)</option>
          <option value="Lax">SameSite=Lax</option>
          <option value="Strict">SameSite=Strict</option>
          <option value="None">SameSite=None</option>
        </select>
      </div>
    </div>
  );
}
